import React, { useEffect, useRef } from 'react'
import KibDivider from '@chewy/kib-divider-react'
import { KibContainer } from '@chewy/kib-layout-react'

import type {
  CompletedRefundTransaction,
  FailedRefundTransaction,
} from '~/api/graphql/generated/types'

import { CancelErrorBanner } from '../../../add-payment-dialog-v2/components/CancelErrorBanner'
import { CancelSuccessBanner } from '../../../add-payment-dialog-v2/components/CancelSuccessBanner'
import { ClientNotes } from '../../../add-payment-dialog-v2/components/ClientNotes'
import { PaymentErrorBanner } from '../../../add-payment-dialog-v2/components/PaymentErrorBanner'
import { usePaymentTransactionStatus } from '../../../add-payment-dialog-v2/hooks/usePaymentTransactionStatus'
import { PartialRefundBanner } from '../components/PartialRefundBanner'
import { RefundDetails } from '../components/RefundDetails'
import { RefundInformation } from '../components/RefundInformation'
import { useRefundDialog } from '../context/RefundDialogProvider'
import {
  isRefundTransactionCompleted,
  isRefundTransactionError,
} from '../utils/refundTransactionUtils'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import '@chewy/kib-divider-styles/dist/kib-divider.css'
import '@chewy/kib-layout-styles/dist/styles/kib-container.css'
import styles from './IssueRefundScreen.module.scss'

export const IssueRefundScreen = () => {
  const { refundInvoice, dialogState, actions } = useRefundDialog()
  const clientId = refundInvoice?.client?.id
  const { transactionId, isLinkedRefundListening, linked } = dialogState

  // Track the last handled transaction to prevent duplicate handling
  const lastHandledTxRef = useRef<string | null>(null)

  // Subscribe to transaction status for linked refunds only
  // This subscription is only active when isLinkedRefundListening is true (after submitLinkedRefund)
  // Note: When skip is true (empty string passed), Apollo returns undefined for data
  const shouldSubscribe =
    linked && isLinkedRefundListening && Boolean(transactionId)
  const { transaction, error } = usePaymentTransactionStatus(
    shouldSubscribe ? transactionId || '' : '',
  )

  // Reset the handled transaction ref when a new transaction is initiated
  useEffect(() => {
    if (transactionId && transactionId !== lastHandledTxRef.current) {
      lastHandledTxRef.current = null
    }
  }, [transactionId])

  // Handle linked refund transaction subscription response
  useEffect(() => {
    if (
      !shouldSubscribe ||
      !transactionId ||
      lastHandledTxRef.current === transactionId
    ) {
      return
    }

    if (transaction) {
      if (isRefundTransactionCompleted(transaction)) {
        lastHandledTxRef.current = transactionId
        actions.handleRefundTransactionSuccess(
          dialogState,
          transaction as CompletedRefundTransaction,
        )
      } else if (isRefundTransactionError(transaction)) {
        lastHandledTxRef.current = transactionId
        actions.handleRefundTransactionFailure(
          dialogState,
          transaction as FailedRefundTransaction,
        )
      }
    } else if (error) {
      lastHandledTxRef.current = transactionId
      actions.handleRefundTransactionHardFailure(dialogState, error)
    }
  }, [shouldSubscribe, transactionId, transaction, error, actions, dialogState])

  return (
    <KibContainer className={styles.issueRefundContainer}>
      <PartialRefundBanner />
      <div className={styles.refundSection}>
        <div className={styles.refundDetailsContainer}>
          <RefundDetails />
          <ClientNotes clientId={clientId} />
        </div>
        <KibDivider className={styles.sectionDivider} />
        {dialogState.cancelSuccessMessage && (
          <CancelSuccessBanner message={dialogState.cancelSuccessMessage} />
        )}
        <div className={styles.refundInformationSection}>
          {dialogState.refundError && (
            <PaymentErrorBanner
              message={dialogState.refundError.message}
              severity={dialogState.refundError.severity}
            />
          )}
          {dialogState.cancelError && !dialogState.refundError && (
            <CancelErrorBanner message={dialogState.cancelError.message} />
          )}
          <RefundInformation />
        </div>
      </div>
    </KibContainer>
  )
}
